import { createSelector } from '@reduxjs/toolkit'

import { ApplicationState } from 'redux/Stores/types'
import { selectAvailableDashboardGroupsIncludeDashboards } from './DashboardGroups'
import { sortAlphabeticalAsc } from 'utils/sortHelper'
import { EAccountRole } from 'types/GlobalAccount'
import type { TDashboardFolderDto } from '@homepal/api-contract'

export const selectOneDashboardFolder = createSelector(
  [
    // Input selectors:
    (state: ApplicationState) => state.DashboardFolderStore.data,
    (state: ApplicationState, id?: string) => id
  ],
  (folders, id) => {
    if (!id) return null

    const folder = folders[id]

    if (folder) return folder

    return null
  }
)

// select all dashboard folders
export const selectAllDashboardFolders = createSelector(
  [
    // Input selectors:
    (state: ApplicationState) => state.DashboardFolderStore.data
  ],
  (data) => Object.values(data)
)

// select dashboard folders fetched state
export const selectDashboardFoldersFetched = createSelector(
  [
    // Input selectors:
    (state: ApplicationState) => state.DashboardFolderStore.fetched
  ],
  (fetched) => fetched
)

/**
 * Get available dashboard folders based on user permissions.
 * - Admins/Homepal users: See all folders
 * - Regular users: See only folders with accessible dashboard groups
 * - Folder updated_at is overridden with latest dashboard group update
 *
 * @param state - Application state
 * @returns Array of available folders sorted alphabetically
 */
export const selectAvailableDashboardFolders = createSelector(
  [
    // Input selectors:
    selectAvailableDashboardGroupsIncludeDashboards,
    (state: ApplicationState) => state.DashboardFolderStore.data,
    (state: ApplicationState) => state.AuthStore.account
  ],
  (groups, folders, user): TDashboardFolderDto[] => {
    if (!user) {
      return []
    }

    const folderArray = Object.values(folders).sort((a, b) =>
      sortAlphabeticalAsc(a.name, b.name)
    )

    // Helper function to get the latest dashboard updated_at for a folder
    const getLatestUpdatedAt = (id: string) => {
      const folderGroups = groups.filter((group) => group.folder_id === id)

      if (folderGroups.length === 0) {
        return null
      }

      return folderGroups.reduce((latest, group) => {
        const groupUpdatedAt = new Date(group.updated_at)

        return groupUpdatedAt > latest ? groupUpdatedAt : latest
      }, new Date(0)) // Start with the earliest possible date
    }

    // If admin or Homepal we return all folders.
    if (user.role === EAccountRole.ADMIN || user.is_homepal_user) {
      return folderArray.map((folder) => ({
        ...folder,
        // Override folder update with latest dashboard update:
        updated_at:
          getLatestUpdatedAt(folder.id)?.toISOString() ?? folder.updated_at
      }))
    }

    // If user we return folders with content for the user.
    return folderArray
      .filter((folder) => {
        return groups.some((group) => group.folder_id === folder.id)
      })
      .map((folder) => ({
        ...folder,
        // Override folder update with latest dashboard update:
        updated_at:
          getLatestUpdatedAt(folder.id)?.toISOString() ?? folder.updated_at
      }))
  }
)
